import { useNavigate, useParams } from 'react-router-dom'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import {
  Box,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  AnalysisAttributes,
  Button,
  Card,
  CardTitle,
  DeleteIcon,
} from 'components'
import { RootState } from 'store'
import { CrudAction, Entity, usePermissions } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { getEditableAttributeSchema } from 'pages/Shipment/validations'
import useDiscardResultsModal from '../../dialogs/useDiscardResultsModal'
// import { AnalysisAttributes } from '../../../../components/AnalysisAttributes/AnalysisAttributes'
import { useDeliveriesService } from '../../../../../../services'
import {
  Delivery,
  INewDelivery,
  TEditableAttribute,
  TShipmentAttributes,
} from '../../../../../../types'
import * as sxStyles from './AnalysisParameters.styled'

interface AnalysisParametersProps {
  delivery?: Delivery
}

export function AnalysisParameters({ delivery }: AnalysisParametersProps) {
  const navigate = useNavigate()
  const { deliveryId, wheatCodeId } = useParams()
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const { updateIncomingDeliveryAttribute } = useDeliveriesService()

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const { setDeliveryAsDiscarded } = useDeliveriesService()
  const acceptedBinId = delivery?.suggested_bins?.filter(
    (bin) => bin?.bin_accepted
  )?.[0]?.id

  const updateDeliveryAttributesMutation = useMutation(
    updateIncomingDeliveryAttribute,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          ['incoming_delivery_attribute'],
          (oldData: Delivery) => ({
            ...oldData,
            attributes: data.result.attributes,
          })
        )

        queryClient.invalidateQueries(['delivery_by_id', deliveryId])
        queryClient.invalidateQueries([
          'deliveries_attribute_comparison',
          deliveryId,
          wheatCodeId,
        ])
        successToast('Delivery attributes was updated succefully')
      },
      onError: (error) => {
        toast.error('An error accured while updating delivery attributes')
        console.error('Failed to update delivery attributes:', error)
      },
    }
  )

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )

  const discardDeliveryMutation = useMutation(
    (deliveryId: number) => setDeliveryAsDiscarded(deliveryId),
    {
      onSuccess: (data) => {
        if (data?.status === true) {
          successToast('Delivery discarded')
          navigate('/shipments')
        }
      },
    }
  )

  const [discardModal, toggleDiscardModal] = useDiscardResultsModal({
    onConfirm: () => {
      discardDeliveryMutation.mutate(delivery?.id)
    },
  })

  const editableAttributes: TEditableAttribute[] =
    delivery?.attributes
      .filter((attr) => attr.is_editable)
      .map((attr) => ({
        name: attr.attribute_name,
        value: attr.value,
      })) || []

  const schema = getEditableAttributeSchema(validationRules)

  const formMethods = useForm<TShipmentAttributes>({
    mode: 'onChange',
    resolver: yupResolver(schema) as unknown as Resolver<TShipmentAttributes>,
    defaultValues: {
      attributes: editableAttributes.reduce(
        (acc, attr) => {
          const sanitizedKey = attr.name.replace(/\s+/g, '_').toLowerCase()
          acc[sanitizedKey] = { value: attr.value }
          return acc
        },
        {} as Record<string, { value: number }>
      ),
    },
  })

  const { handleSubmit } = formMethods

  const onSubmit = (data: TShipmentAttributes) => {
    const updatedAttributes = delivery?.attributes.map((attribute) => {
      if (attribute.is_editable) {
        const attributeKey = attribute.attribute_name
          .toLowerCase()
          .replace(' ', '_')
        const updatedValue = data.attributes[attributeKey]?.value
        const parsedValue = updatedValue ? Number(updatedValue) : null
        return {
          ...attribute,
          value: parsedValue,
        }
      } else {
        return attribute
      }
    })

    const updatedDelivery: INewDelivery = {
      id: delivery?.id,
      name: delivery?.name,
      wheat_code_id: delivery?.wheat_code_id,
      company_id: delivery?.company_id,
      consistency_id: delivery?.consistency_id,
      discarded: delivery?.discarded,
      attributes: updatedAttributes,
    }
    updateDeliveryAttributesMutation.mutate(updatedDelivery)
  }

  return (
    <Card
      sx={
        isSmallScreen ? sxStyles.mainCrdStyleSmallScreen : sxStyles.mainCrdStyle
      }
    >
      <Box>
        <CardTitle title="Analysis parameters" />
      </Box>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AnalysisAttributes
            attributes={delivery?.attributes}
            defaultWheatCodeAttributes={delivery?.wheat_code?.attributes}
          />
          <Stack sx={sxStyles.stackWrapperStyle}>
            <Stack sx={sxStyles.stackBoxWrapper}>
              <Box sx={sxStyles.boxButtonsWrapper}>
                <Button
                  disabled={
                    !!acceptedBinId || delivery?.discarded
                      ? true
                      : !checkPermission(Entity.DELIVERY, CrudAction.UPDATE)
                  }
                  onClick={toggleDiscardModal}
                  variantType="secondary"
                  startIcon={<DeleteIcon size={20} />}
                >
                  {delivery?.discarded ? 'Discarded' : 'Discard Results'}
                </Button>
                {checkPermission(Entity.DELIVERY, CrudAction.UPDATE) && (
                  <Button
                    type="submit"
                    variantType="primary"
                    disabled={updateDeliveryAttributesMutation.isLoading}
                    sx={sxStyles.applyBtnStyle}
                  >
                    {updateDeliveryAttributesMutation.isLoading ? (
                      <CircularProgress size={24} color="primary" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                )}
              </Box>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
      {discardModal}
    </Card>
  )
}
